import {useCallback, useEffect, useRef} from 'react'
import {useLocation, useSearchParams} from 'react-router-dom'

/**
 * sessionStorage-backed filter persistence (issue 342.23, level B).
 *
 * Behaviour:
 *   - First mount per pathname: if the URL has any params, they win and get
 *     mirrored to storage. If the URL is empty AND storage has an entry,
 *     hydrate from storage (replace, not push, so browser-back doesn't
 *     visit the empty pre-hydrate URL).
 *   - Every subsequent URL change mirrors to storage — including the case
 *     where the user clicks Clear All and the URL becomes empty. That
 *     wipes the storage entry too, so the next mount starts clean.
 *   - {@link clear} (intended as an explicit hook caller) wipes both the
 *     URL params and the storage entry.
 *   - Storage is keyed by pathname so different pages don't cross-contaminate.
 *   - Per-session lifetime (sessionStorage) — clears on logout / browser
 *     close. Matches the AppConfig session-scoping pattern (spec §4.10.1).
 */
export const FILTER_PERSISTENCE_KEY_PREFIX = 'iam.filter:'

export interface FilterStatePersistenceApi {
    /** Wipe both the URL params and the stored entry for this page. */
    clear: () => void
}

export function useFilterStatePersistence(): FilterStatePersistenceApi {
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    // Track which pathname we have already done the mount-time hydrate for,
    // so subsequent searchParams changes don't keep re-hydrating from storage.
    const hydratedFor = useRef<string | null>(null)

    const storageKey = `${FILTER_PERSISTENCE_KEY_PREFIX}${location.pathname}`
    const urlSerialized = searchParams.toString()

    useEffect(() => {
        if (hydratedFor.current !== location.pathname) {
            // First mount on this pathname — decide URL vs storage.
            hydratedFor.current = location.pathname
            if (urlSerialized.length === 0) {
                const stored = sessionStorage.getItem(storageKey)
                if (stored && stored.length > 0) {
                    setSearchParams(new URLSearchParams(stored), {replace: true})
                    return  // next render will mirror the now-non-empty URL
                }
            }
        }
        // Mirror current URL to storage. Empty URL deletes the storage entry
        // so a Clear All wipes both surfaces.
        if (urlSerialized.length === 0) {
            sessionStorage.removeItem(storageKey)
        } else {
            sessionStorage.setItem(storageKey, urlSerialized)
        }
    // setSearchParams is stable. Re-run on pathname or URL change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, urlSerialized])

    const clear = useCallback(() => {
        sessionStorage.removeItem(storageKey)
        setSearchParams(new URLSearchParams(), {replace: true})
    }, [storageKey, setSearchParams])

    return {clear}
}
